/**
 * IacpClient — federation-aware client for the cloud-api queue.
 *
 * Workflow:
 *  1. findServer() picks the right cloud-api node for the target session
 *     (honours explicit `server` param → local probe → federation lookup
 *     via CLOUD_API_BOOTSTRAP /api/sessions/all → fallback to bootstrap)
 *  2. sendMessage() POSTs to /api/queue/{session}/message and returns msgId
 *  3. pollResponse() polls /api/queue/{session}/message/{msgId} every 2s
 *     until status=done or 120s timeout
 *
 * Stateful — holds the resolved API base URL after findServer().
 * Preserves the exact same REST contract as the old gps-tracker.html so
 * the Guide bot sees no difference.
 */
export class IacpClient {
    constructor(_config, _bus) {
        this._config = _config;
        this._bus = _bus;
        this._apiBase = '';
        this._resolved = false;
        /** ai mode — 'pool' (default, AgentOS pool path) or 'direct' (OpenAI
         *  via guide-api /api/v1/ai/generate). See ADR-001 for the dual-mode
         *  rationale. */
        this._aiMode = 'pool';
        /** When in direct mode, where to POST. Set together with aiMode. */
        this._directEndpoint = '';
        /** Optional guide-api base URL. When set AND the caller passes
         *  enrichment params to sendMessage, we route the outbound POST
         *  through guide-api /api/v1/live-send so the track_enricher runs
         *  on EVERY live tick (same XML shape as the probe path — eliminates
         *  the live-vs-probe divergence the Tick-Konsistenz bug exposed).
         *  Polling stays direct to cloud-api so SSE/bfcache recovery is
         *  unchanged. */
        this._enricherBase = '';
        /** Stash for direct-mode results — keyed by the synthetic msg id
         *  sendMessage returned. pollResponse drains it. */
        this._directResults = new Map();
        /** Fast-First-Turn (opt-in): route the FIRST enricher send of this client
         *  through guide-api /api/v1/live-send-fast — same request shape, but the
         *  narration comes back SYNC from a fast direct model (~2-4s total) instead
         *  of the 20-50s pool first-turn; guide-api warms the pool in parallel so
         *  turn 2 goes the normal path. Falls back to /live-send on any error. */
        this._fastFirstTurn = false;
        this._fastFirstTried = false;
        /** Errors from direct-mode sendMessage that pollResponse should surface. */
        this._directErrors = new Map();
        /** Active SSE streams for in-flight pollResponse() calls, keyed by msgId.
         *  Used by the bfcache / visibility recovery path: when the browser
         *  restores a tab from background the EventSource is silently dead and
         *  the caller would otherwise wait the full 240s timeout. */
        this._activeStreams = new Map();
        /** When the tab last went hidden — used by the visibilitychange
         *  listener to decide whether the gap is long enough to suspect a
         *  dead stream. Short blips (lock screen, app switch <30s) are
         *  ignored. */
        this._hiddenSince = 0;
        this._installRecoveryHooks();
    }
    /** Wire pageshow + visibilitychange listeners so the lib can recover
     *  in-flight SSE streams when the browser restores a stale tab from
     *  bfcache or wakes up after a long hidden period. iOS Safari is the
     *  worst offender: a tab from yesterday gets restored, the
     *  EventSource we opened then is gone, but no onerror fires — we'd
     *  wait the full timeout while the bot already answered. */
    _installRecoveryHooks() {
        if (typeof window === 'undefined' || typeof document === 'undefined')
            return;
        window.addEventListener('pageshow', (e) => {
            // persisted=true means bfcache restore — definitely lost any
            // EventSource we had. iOS Safari is the main producer of this.
            if (e.persisted) {
                this._reconnectAllStreams('bfcache-restore');
            }
        });
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this._hiddenSince = Date.now();
                return;
            }
            if (document.visibilityState === 'visible' && this._hiddenSince > 0) {
                const hiddenMs = Date.now() - this._hiddenSince;
                this._hiddenSince = 0;
                // 30s threshold: short hidden periods (lock screen flick, app
                // switcher peek) don't kill SSE on most browsers. Longer means
                // the OS may have suspended the JS context.
                if (hiddenMs > 30000 && this._activeStreams.size > 0) {
                    this._reconnectAllStreams(`visible-after-${Math.round(hiddenMs / 1000)}s-hidden`);
                }
            }
        });
    }
    /** Force-reconnect every in-flight SSE stream. Each registered stream
     *  knows how to rebuild itself; this just iterates and asks them to. */
    _reconnectAllStreams(reason) {
        if (this._activeStreams.size === 0)
            return;
        this._bus.emit('iacp:stream-recovery', { reason, count: this._activeStreams.size });
        for (const [, conn] of this._activeStreams) {
            conn.reconnect(reason);
        }
    }
    /** Switch between pool and direct AI generation. Safe to call at any
     *  time; in-flight messages already on the pool path will finish via
     *  the pool, new messages will go through the chosen mode. */
    setAiMode(mode, directEndpoint) {
        this._aiMode = mode;
        if (directEndpoint)
            this._directEndpoint = directEndpoint;
        this._bus.emit('iacp:ai-mode-changed', { mode, directEndpoint: this._directEndpoint });
    }
    get aiMode() { return this._aiMode; }
    /** Set the guide-api base URL used by the live-send enricher path.
     *  When unset, sendMessage always goes direct to cloud-api (the
     *  legacy behaviour). Caller normally wires this once at startup
     *  from the app's GpsTrackerConfig. */
    setEnricherBase(url) {
        this._enricherBase = url.replace(/\/+$/, '');
        this._bus.emit('iacp:enricher-base-changed', { url: this._enricherBase });
    }
    get enricherBase() { return this._enricherBase; }
    /** Opt-in Fast-First-Turn (see _fastFirstTurn). App-level default stays off
     *  so V1 consumers of the lib keep their exact behavior. */
    setFastFirstTurn(on) {
        this._fastFirstTurn = on;
    }
    /** Auto-detect which cloud-api node hosts the target session */
    async findServer() {
        const { session, server, cloudApiBootstrap } = this._config;
        // 1. Explicit override via query param wins
        if (server) {
            this._apiBase = server;
            this._resolved = true;
            this._bus.emit('iacp:server-resolved', { apiBase: this._apiBase });
            return;
        }
        // 2. Bootstrap node: does it host the session locally?
        try {
            const r = await fetch(cloudApiBootstrap + '/api/sessions');
            const d = await r.json();
            const local = Array.isArray(d?.sessions) && d.sessions.some((s) => s.name === session);
            if (local) {
                this._apiBase = cloudApiBootstrap;
                this._resolved = true;
                this._bus.emit('iacp:server-resolved', { apiBase: this._apiBase });
                return;
            }
        }
        catch { }
        // 3. Federation: ask bootstrap for all known sessions cluster-wide
        try {
            const r = await fetch(cloudApiBootstrap + '/api/sessions/all');
            const d = await r.json();
            const hit = (Array.isArray(d?.sessions) ? d.sessions : []).find((x) => x.name === session);
            if (hit && hit.node_url) {
                this._apiBase = hit.node_url;
                this._resolved = true;
                this._bus.emit('iacp:server-resolved', { apiBase: this._apiBase });
                return;
            }
        }
        catch { }
        // 4. Final fallback: use bootstrap anyway so queue POSTs route somewhere
        this._apiBase = cloudApiBootstrap;
        this._resolved = true;
        this._bus.emit('iacp:server-resolved', {
            apiBase: this._apiBase,
            fallback: true,
        });
    }
    get apiBase() {
        return this._apiBase;
    }
    get isResolved() {
        return this._resolved;
    }
    /** POST a message and return the assigned msgId.
     *
     *  Pool mode: POSTs to /api/queue/{session}/message on the resolved
     *  cloud-api node and returns the queue-assigned id (pollResponse
     *  picks up the answer over SSE / polling).
     *
     *  Direct mode: POSTs to the configured guide-api endpoint and stashes
     *  the parsed response under a synthetic id. pollResponse resolves
     *  from that stash immediately — no SSE, no polling.
     *
     *  Enricher path: when `enrich` is supplied AND `enricherBase` is
     *  configured, POST to guide-api /api/v1/live-send instead. guide-api
     *  injects the track_enricher's <places> + <artrack_context> blocks
     *  and forwards to cloud-api. We get back {msg_id, api_base}; polling
     *  uses the returned api_base so it lands on the right cloud-api node
     *  regardless of what findServer() picked. */
    async sendMessage(text, userId, enrich) {
        if (this._aiMode === 'direct') {
            return this._sendDirect(text, userId);
        }
        if (enrich && this._enricherBase) {
            return this._sendViaEnricher(text, userId, enrich);
        }
        if (!this._resolved) {
            throw new Error('IacpClient: findServer() must be called before sendMessage()');
        }
        const body = { text, user_id: userId };
        const t0 = Date.now();
        let res;
        try {
            res = await fetch(`${this._apiBase}/api/queue/${this._config.session}/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._bus.emit('iacp:send-error', {
                phase: 'fetch',
                elapsedMs: Date.now() - t0,
                error: msg,
                session: this._config.session,
            });
            throw new Error(`IacpClient.sendMessage: network error: ${msg}`);
        }
        const elapsedMs = Date.now() - t0;
        if (!res.ok) {
            let bodyPreview = '';
            try {
                bodyPreview = (await res.text()).slice(0, 200);
            }
            catch { /* ignore */ }
            this._bus.emit('iacp:send-error', {
                phase: 'http',
                elapsedMs,
                status: res.status,
                body: bodyPreview,
                session: this._config.session,
            });
            throw new Error(`IacpClient.sendMessage: HTTP ${res.status} ${bodyPreview ? `body="${bodyPreview}"` : ''}`);
        }
        const data = (await res.json());
        this._bus.emit('iacp:send-ok', {
            msgId: data.id,
            elapsedMs,
            session: this._config.session,
        });
        return data.id;
    }
    /** Enricher-path send: POST to guide-api /api/v1/live-send. guide-api
     *  injects <places> + <artrack_context> from track_enricher and
     *  enqueues the enriched message on cloud-api. We get back the
     *  cloud-api msg_id + api_base; we rebase _apiBase so pollResponse
     *  hits the same node guide-api landed on.
     *
     *  Lat/lon/trackId/persona come in via `enrich` because the XML body
     *  is opaque to us at this layer — guide-api needs structured fields
     *  for the track_enricher call. */
    async _sendViaEnricher(text, userId, enrich) {
        // Fast-First-Turn: exactly ONE attempt per client instance; any failure
        // falls through to the normal /live-send below (no dead first turn).
        if (this._fastFirstTurn && !this._fastFirstTried) {
            this._fastFirstTried = true;
            try {
                return await this._sendFastFirst(text, userId, enrich);
            }
            catch (err) {
                this._bus.emit('iacp:fast-first-fallback', {
                    error: err instanceof Error ? err.message : String(err),
                    session: this._config.session,
                });
            }
        }
        const url = `${this._enricherBase}/api/v1/live-send`;
        const body = {
            session: this._config.session,
            raw_xml: text,
            lat: enrich.lat,
            lon: enrich.lon,
            track_id: enrich.trackId ?? null,
            persona: enrich.persona ?? null,
            user_id: userId,
        };
        const t0 = Date.now();
        let res;
        try {
            res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            this._bus.emit('iacp:send-error', {
                phase: 'fetch-enricher',
                elapsedMs: Date.now() - t0,
                error: msg,
                session: this._config.session,
            });
            throw new Error(`IacpClient.sendMessage(enricher): network error: ${msg}`);
        }
        const elapsedMs = Date.now() - t0;
        if (!res.ok) {
            let bodyPreview = '';
            try {
                bodyPreview = (await res.text()).slice(0, 200);
            }
            catch { /* ignore */ }
            this._bus.emit('iacp:send-error', {
                phase: 'http-enricher',
                elapsedMs,
                status: res.status,
                body: bodyPreview,
                session: this._config.session,
            });
            throw new Error(`IacpClient.sendMessage(enricher): HTTP ${res.status} ${bodyPreview ? `body="${bodyPreview}"` : ''}`);
        }
        const data = (await res.json());
        // Rebase _apiBase so pollResponse hits the cloud-api node where the
        // message landed. live-send always returns the federation root
        // (cloud-api.arkturian.com) which forwards to the owning node.
        this._apiBase = data.api_base.replace(/\/+$/, '');
        this._resolved = true;
        const msgIdNum = Number(data.msg_id);
        this._bus.emit('iacp:send-ok', {
            msgId: msgIdNum,
            elapsedMs,
            session: this._config.session,
            enriched: data.enriched,
            poisAhead: data.pois_ahead_count ?? 0,
            onTrack: data.on_track ?? null,
            kmProgress: data.km_progress ?? null,
            phase: data.phase ?? null,
        });
        return msgIdNum;
    }
    /** Fast-First-Turn send: POST guide-api /api/v1/live-send-fast — same body
     *  as /live-send, but the response is SYNC in the /ai/generate shape
     *  ({id, status:'done', response}). The narration is parsed here and stashed
     *  under a synthetic msgId; pollResponse drains the stash instantly (same
     *  mechanics as direct mode). guide-api fires the pool-acquire in parallel
     *  server-side, so turn 2 goes the normal warm path. */
    async _sendFastFirst(text, userId, enrich) {
        const url = `${this._enricherBase}/api/v1/live-send-fast`;
        const t0 = Date.now();
        // Hard ceiling against hangs: steady total is ~3-4s (measured); a stuck
        // fast call must not eat the latency win — abort → normal /live-send.
        const abort = new AbortController();
        const abortTimer = setTimeout(() => abort.abort(), 8000);
        let res;
        try {
            res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session: this._config.session,
                    raw_xml: text,
                    lat: enrich.lat,
                    lon: enrich.lon,
                    track_id: enrich.trackId ?? null,
                    persona: enrich.persona ?? null,
                    user_id: userId,
                }),
                signal: abort.signal,
            });
        }
        finally {
            clearTimeout(abortTimer);
        }
        if (!res.ok) {
            const bodyPreview = (await res.text().catch(() => '')).slice(0, 200);
            throw new Error(`live-send-fast HTTP ${res.status} ${bodyPreview}`);
        }
        const data = (await res.json());
        if (data.status !== 'done' || typeof data.response !== 'string') {
            throw new Error(`live-send-fast bad shape: ${JSON.stringify(data).slice(0, 200)}`);
        }
        const msgId = Date.now();
        const parsed = IacpClient._parseResponse(msgId, data.response);
        this._directResults.set(msgId, parsed);
        this._bus.emit('iacp:send-ok', {
            msgId,
            elapsedMs: Date.now() - t0,
            session: this._config.session,
            fastFirst: true,
            model: data.model,
            kmProgress: data.km_progress ?? null,
            phase: data.phase ?? null,
        });
        return msgId;
    }
    /** Direct-mode send: kicks off the OpenAI call in the background and
     *  returns a synthetic msgId immediately. pollResponse drains the
     *  result map. We don't await the fetch here so the call shape
     *  matches Pool (sendMessage returns fast, pollResponse blocks). */
    _sendDirect(text, userId) {
        if (!this._directEndpoint) {
            throw new Error('IacpClient: setAiMode("direct", endpoint) must include directEndpoint');
        }
        const msgId = Date.now();
        void fetch(this._directEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ text, user_id: userId }),
        })
            .then(async (r) => {
            if (!r.ok) {
                const body = await r.text().catch(() => '');
                throw new Error(`HTTP ${r.status}: ${body.slice(0, 200)}`);
            }
            const data = await r.json();
            if (data.status !== 'done' || typeof data.response !== 'string') {
                throw new Error(`Bad direct response shape: ${JSON.stringify(data).slice(0, 200)}`);
            }
            const parsed = IacpClient._parseResponse(msgId, data.response);
            this._directResults.set(msgId, parsed);
        })
            .catch((err) => {
            this._directErrors.set(msgId, err.message);
        });
        return msgId;
    }
    /**
     * Poll for a response to a previously sent message.
     * Resolves with the parsed IacpResponse when status=done.
     * Rejects after timeoutMs (default 240s).
     *
     * Claude Sonnet 4.6 in high-effort mode cold-starts at ~40-60s per
     * message; add artrack MCP tool calls on top (10-20s) and the old
     * 120s window was too tight. 240s gives plenty of slack without
     * blocking forever on genuinely stuck sessions.
     */
    // 780s: cloud-api's rescue-or-expire sweep resolves stuck messages
    // after ~720s+8s (Cloud, 2026-06-12) — polling must outlive that,
    // otherwise the UI gives up on answers the server still delivers.
    // The old 240s predated the sweep and orphaned recoverable replies.
    pollResponse(msgId, timeoutMs = 780000) {
        // Fast-First-Turn: the narration already arrived synchronously and sits in
        // the stash — resolve instantly, no SSE/poll roundtrip. The 'iacp:response'
        // emit is load-bearing: AudioGuide's response handler (topic card + TTS)
        // hangs off the bus, not off pollResponse's return value.
        const stashed = this._directResults.get(msgId);
        if (stashed) {
            this._directResults.delete(msgId);
            this._bus.emit('iacp:response', stashed);
            return Promise.resolve(stashed);
        }
        if (this._aiMode === 'direct') {
            return this._pollDirect(msgId, timeoutMs);
        }
        if (!this._resolved) {
            return Promise.reject(new Error('IacpClient: findServer() must be called before pollResponse()'));
        }
        // Prefer SSE stream (instant push) with polling fallback
        const streamUrl = `${this._apiBase}/api/queue/${this._config.session}/message/${msgId}/stream`;
        const pollUrl = `${this._apiBase}/api/queue/${this._config.session}/message/${msgId}`;
        return new Promise((resolve, reject) => {
            let settled = false;
            const startTs = Date.now();
            // Pending-too-long warnings — surface "queue accepted us but worker
            // isn't processing" symptoms early, instead of silent 240s timeout.
            // Two thresholds: 60s = "this is taking unusually long" (warm pool
            // medium effort responds in 20-50s typically), 120s = "very likely
            // the worker is stuck or the bot is down".
            const warn60 = setTimeout(() => {
                if (settled)
                    return;
                this._bus.emit('iacp:pending-warning', {
                    msgId,
                    elapsedMs: Date.now() - startTs,
                    severity: 'slow',
                    session: this._config.session,
                });
            }, 60000);
            const warn120 = setTimeout(() => {
                if (settled)
                    return;
                this._bus.emit('iacp:pending-warning', {
                    msgId,
                    elapsedMs: Date.now() - startTs,
                    severity: 'stuck',
                    session: this._config.session,
                });
            }, 120000);
            const settle = (parsed) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(warn60);
                clearTimeout(warn120);
                this._activeStreams.delete(msgId);
                this._bus.emit('iacp:response', parsed);
                resolve(parsed);
            };
            const fail = (err) => {
                if (settled)
                    return;
                settled = true;
                clearTimeout(warn60);
                clearTimeout(warn120);
                this._activeStreams.delete(msgId);
                reject(err);
            };
            // Timeout guard
            const timer = setTimeout(() => {
                fail(new Error(`IacpClient.pollResponse: timeout after ${timeoutMs}ms (msgId=${msgId})`));
            }, timeoutMs);
            if (typeof EventSource === 'undefined') {
                // No EventSource support — use polling directly
                this._pollFallback(pollUrl, msgId, startTs, timeoutMs, timer, settle, fail);
                return;
            }
            // SSE with reconnect-capable wrapper. The reconnect closure
            // can be triggered by _reconnectAllStreams when the tab comes
            // back from bfcache / long-hidden; it tears down the dead
            // EventSource and opens a fresh one targeting the same msgId.
            // The cloud-api stream endpoint will replay any state that
            // arrived while we were disconnected (status=done is sticky).
            const conn = {
                es: null,
                reconnect: (_reason) => { },
            };
            const openStream = (reason) => {
                if (settled)
                    return;
                if (conn.es) {
                    try {
                        conn.es.close();
                    }
                    catch { /* ignore */ }
                    conn.es = null;
                }
                const es = new EventSource(streamUrl);
                conn.es = es;
                es.onmessage = (ev) => {
                    try {
                        const d = JSON.parse(ev.data);
                        if (d.status === 'done' && d.response) {
                            try {
                                es.close();
                            }
                            catch { /* ignore */ }
                            clearTimeout(timer);
                            settle(IacpClient._parseResponse(msgId, d.response));
                        }
                        else if (d.status === 'error') {
                            try {
                                es.close();
                            }
                            catch { /* ignore */ }
                            clearTimeout(timer);
                            fail(new Error(`Guide error: ${d.error || 'unknown'}`));
                        }
                    }
                    catch { /* ignore malformed events */ }
                };
                es.onerror = () => {
                    // SSE failed — close and fall back to polling. The activeStreams
                    // entry stays registered so bfcache-recovery can still come in,
                    // but the open polling tick is the actual progress driver now.
                    try {
                        es.close();
                    }
                    catch { /* ignore */ }
                    conn.es = null;
                    if (!settled) {
                        this._pollFallback(pollUrl, msgId, startTs, timeoutMs, timer, settle, fail);
                    }
                };
                if (reason !== 'initial') {
                    // Diagnostic — visible in F2 debug log so we can see recovery
                    // events as they happen in the real world.
                    this._bus.emit('log:message', {
                        level: 'info',
                        text: `SSE reconnect (msgId=${msgId}, reason=${reason})`,
                        timestamp: Date.now(),
                    });
                }
            };
            conn.reconnect = openStream;
            this._activeStreams.set(msgId, conn);
            openStream('initial');
        });
    }
    /** Direct-mode poll: 50ms polling of the in-memory result map populated
     *  by _sendDirect. Same timeout contract as the pool poll so callers
     *  don't need to special-case. */
    _pollDirect(msgId, timeoutMs) {
        return new Promise((resolve, reject) => {
            const startTs = Date.now();
            const tick = () => {
                const hit = this._directResults.get(msgId);
                if (hit) {
                    this._directResults.delete(msgId);
                    this._bus.emit('iacp:response', hit);
                    resolve(hit);
                    return;
                }
                const err = this._directErrors.get(msgId);
                if (err) {
                    this._directErrors.delete(msgId);
                    reject(new Error(`Direct AI error: ${err}`));
                    return;
                }
                if (Date.now() - startTs > timeoutMs) {
                    reject(new Error(`IacpClient.pollResponse: timeout after ${timeoutMs}ms (direct, msgId=${msgId})`));
                    return;
                }
                setTimeout(tick, 50);
            };
            tick();
        });
    }
    /** Polling fallback when SSE is unavailable */
    _pollFallback(url, msgId, startTs, timeoutMs, timer, settle, fail) {
        const tick = async () => {
            if (Date.now() - startTs > timeoutMs)
                return; // timeout guard handles this
            try {
                const r = await fetch(url);
                const d = await r.json();
                if (d.status === 'done' && d.response) {
                    clearTimeout(timer);
                    settle(IacpClient._parseResponse(msgId, d.response));
                    return;
                }
                else if (d.status === 'error') {
                    clearTimeout(timer);
                    fail(new Error(`Guide error: ${d.response || 'unknown'}`));
                    return;
                }
            }
            catch { /* transient — retry */ }
            setTimeout(tick, 2000);
        };
        setTimeout(tick, 2000);
    }
    /** Parse Guide's response body — JSON payload or raw text fallback.
     *  Runs the tmux pane-wrap repair tokenizer first (mirrors
     *  guide-api/pool_client.py) so wraps inside JSON keys / URLs survive
     *  the SSE round-trip. Idempotent on well-formed minified JSON. */
    static _parseResponse(msgId, raw) {
        const base = { id: msgId, raw };
        try {
            const repaired = IacpClient._dewrapOutsideStrings(raw);
            const parsed = JSON.parse(repaired);
            const obj = IacpClient._collapseInstringWraps(parsed);
            return {
                ...base,
                content: typeof obj.content === 'string' ? obj.content : undefined,
                destination: obj.destination && typeof obj.destination === 'object' ? obj.destination : undefined,
                song: typeof obj.song === 'string' ? obj.song : undefined,
                pois: Array.isArray(obj.pois)
                    ? obj.pois.map((p) => ({ ...p, title: p.title || p.name || 'POI' }))
                    : undefined,
                vibe: obj.vibe,
                topic: obj.topic && typeof obj.topic === 'object' ? obj.topic : undefined,
                // Pass through additional_topics so the queue can play them
                // sequentially after main. Strict shape check: each must have
                // content + (optionally) topic + vibe.
                additional_topics: Array.isArray(obj.additional_topics)
                    ? obj.additional_topics
                        .filter((a) => a && typeof a === 'object' && typeof a.content === 'string')
                        .map((a) => ({
                        content: a.content,
                        vibe: a.vibe,
                        topic: a.topic && typeof a.topic === 'object' ? a.topic : undefined,
                    }))
                    : undefined,
            };
        }
        catch {
            // Not JSON — check if it looks like a real spoken response vs
            // Claude Code UI artifacts (auto-update, crunched, MCP tool output).
            // Only speak text that looks like natural language.
            const noise = /auto-update|crunched for|claude doctor|npm i -g|MCP\)|ctrl\+o|memory.*expand|file.*expand|✻|✗|⎿/i;
            if (noise.test(raw) || raw.length < 10) {
                return base; // no content — TTS will skip
            }
            return { ...base, content: raw };
        }
    }
    /** Repair tmux pane-wrap artifacts in a JSON candidate before parse.
     *  Mirror of guide-api/pool_client.py::_dewrap_outside_strings.
     *  Three passes:
     *    1+2. char-walk: outside string literals strip whitespace
     *         (JSON-safe); inside string literals replace \n\r\t with a
     *         single space (pane-wraps would otherwise leak as control
     *         chars which are non-conformant JSON anyway).
     *    3.   collapse whitespace inside JSON keys (bot-spec keys are
     *         identifiers — `"im age":` → `"image":`).
     *  Idempotent on well-formed minified JSON. */
    static _dewrapOutsideStrings(raw) {
        let out = '';
        let inString = false;
        let escape = false;
        for (let i = 0; i < raw.length; i++) {
            const ch = raw[i];
            if (escape) {
                out += ch;
                escape = false;
                continue;
            }
            if (inString) {
                if (ch === '\\') {
                    out += ch;
                    escape = true;
                    continue;
                }
                if (ch === '"') {
                    inString = false;
                    out += ch;
                    continue;
                }
                if (ch === '\n' || ch === '\r' || ch === '\t') {
                    out += ' ';
                    continue;
                }
                out += ch;
                continue;
            }
            // outside string
            if (ch === '"') {
                inString = true;
                out += ch;
                continue;
            }
            if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r')
                continue;
            out += ch;
        }
        return out.replace(/"([^"\\]*(?:\\.[^"\\]*)*)"\s*:/g, (_m, key) => `"${key.replace(/\s+/g, '')}":`);
    }
    /** Recursively replace `\n[ \t]*` inside string values with a single
     *  space. Pane-wraps mid-value would otherwise leave a literal
     *  newline in TTS / topic-card text. */
    static _collapseInstringWraps(obj) {
        if (typeof obj === 'string') {
            return obj.replace(/\n[ \t]*/g, ' ');
        }
        if (Array.isArray(obj)) {
            return obj.map(v => IacpClient._collapseInstringWraps(v));
        }
        if (obj && typeof obj === 'object') {
            const result = {};
            for (const [k, v] of Object.entries(obj)) {
                result[k] = IacpClient._collapseInstringWraps(v);
            }
            return result;
        }
        return obj;
    }
}
//# sourceMappingURL=IacpClient.js.map